import {
  Component,
  Prop,
  Event,
  h,
  type EventEmitter,
  State,
  Listen,
} from '@stencil/core';
import guidedSearchStore from '../../../guided/guided-search-store';
import { ConversationalSearchBannerSimple } from '../conversational-search-banners/simple-banner/simple-banner';
import { ConversationalSearchBannerLarge } from '../conversational-search-banners/large-banner/large-banner';
import { bannersCallback, getCtaText } from './conversational-search-banner-mediator-helpers';
import { getCtaActionSessionStorageValue, getTermsAndConditionsSessionStorageValue } from '../conversational-search-logic/storage-manager/storage-manager-helpers';
import type { labelProviderInterface } from '../../../LabelProviderInterface';

@Component({
  tag: 'uiaas-search-widget-conversational-search-banner-mediator',
  styleUrl: 'conversational-search-banner-mediator.scss',
  shadow: true,
})
export class WidgetConversationalSearchBannerMediator {
  @Prop() readonly labelProvider: labelProviderInterface;
  @Prop() readonly isGuidedVariant: boolean = false;
  @Prop() readonly isZeroResult: boolean = false;
  @Prop() readonly isInnerTab: boolean = false;
  @Prop() readonly country?: string;
  @Prop() readonly language?: string;
  @Prop() readonly term?: string;
  @Prop() readonly conversationalSearchProductsEndpoint?: string;
  @Prop() readonly isDynamicBannerActive: boolean = false;
  @Prop() readonly apigeeAuth: string;
  @Prop() readonly apigeeTokenUrl: string;
  @Prop() readonly conversationalSearchBrandUrl: string;

  @State() isBannerVisible: boolean = true;

  @Event({
    eventName: 'conversational-search:ga-cta-banner-clicked',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    gaCtaBannerClickedEventEmitter: EventEmitter<{
    isZeroResultPage: boolean;
    isGuided: boolean;
    keyword: string;
    clickedArea: string;
    isRefinement: boolean;
  }>;

  @Event({
    eventName: 'conversational-search:cta-action',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    conversationalSearchCtaActionEventEmitter: EventEmitter<{
    isChatVisible: boolean;
    isGuidedSearch: boolean;
  }>;

  @Listen('conversational-search:chat-visibility', { target: 'window' })
  handleCtaActions() {
      this.isBannerVisible = false;
  }

  @Listen('conversational-search:user-end-of-conversation', { target: 'window' })
  handleEndOfConversationAction() {
      this.isBannerVisible = true;
  }

  private selectBanner = () => {
      const gsTermPhraseLength = guidedSearchStore.get('gsTerm')?.length;
      const ctaText = getCtaText(this.labelProvider);
      const tagText = this.labelProvider('conversational.search.label.genai', 'Generative AI');

      if (this.isGuidedVariant && gsTermPhraseLength >= 3 && !getTermsAndConditionsSessionStorageValue())
        return (
          <ConversationalSearchBannerSimple
            labelProvider={(e, f) => this.labelProvider(e, f)}
            language={this.language}
            country={this.country}
            callback={bannersCallback(this)}
          />
        );

      if (!this.isGuidedVariant && this.isInnerTab)
        return (
          <section class={{'conversational-search-banner-mediator--hidden': !this.isBannerVisible || getCtaActionSessionStorageValue()}}>
            <ConversationalSearchBannerLarge
              labelProvider={(e, f) => this.labelProvider(e, f)}
              term={this.term}
              callback={bannersCallback(this)}
              ctaText={ctaText}
              tagText={tagText}
            />
          </section>
        );

      if (!this.isGuidedVariant && !this.isInnerTab)
        return (
          <uiaas-search-widget-conversational-search-dynamic-banners-mediator
            labelProvider={(e, f) => this.labelProvider(e, f)}
            term={this.term}
            callback={bannersCallback(this)}
            conversationalSearchProductsEndpoint={this.conversationalSearchProductsEndpoint}
            isDynamicBannerActive={this.isDynamicBannerActive}
            apigeeAuth={this.apigeeAuth}
            apigeeTokenUrl={this.apigeeTokenUrl}
            language={this.language}
            country={this.country}
            isZeroResult={this.isZeroResult}
            conversationalSearchBrandUrl={this.conversationalSearchBrandUrl}
            ctaText={ctaText}
            tagText={tagText}
          />
        );
  };

  render() {
      return this.selectBanner();
  }
}
